import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Dialog, DialogContent, Skeleton, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { Add, DeleteOutlineOutlined } from "@mui/icons-material";
import { AccordionCard, Button, Callout, FieldWrapper, PageHeader, StepActions, UploadCard } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetBanksByCodeQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import {
  useDeleteBankAccountMutation,
  useDeleteBankAccountStatementMutation,
  useLazyGetBankAccountsQuery,
  usePostBankAccountStatementMutation,
  usePostBankAccountsMutation,
  usePutApplicationCompletedMutation,
  usePutBankAccountMutation,
} from "@api/applications";
import { useLazyGetDocumentAppIdQuery, usePatchDocumentMutation } from "@api/documents";
import { BankAccountFormItem } from "@interfaces/catalogs";
import { PayloadBankAccount } from "@interfaces/request";
import { ApiError, DocumentResponse } from "@interfaces/response";
import {
  ALTO_SKELETON_EXPEDIENTE,
  CLABE_LENGTH,
  DOCUMENTS_CATALOG,
  PATHS_EXPEDIENTE_V2,
  INVALID_BANK_ACCOUNT_CODE,
  DEFAULT_NUMBER_BANK_STATEMENTS,
} from "@helpers/constantes";
import {
  createEmptyBankAccount,
  getBankInformationConfig,
  getFormikArrayError,
  identifyBankAccounts,
} from "@helpers/funciones";
import { UUID } from "crypto";

const PATH_EXPEDIENTE = PATHS_EXPEDIENTE_V2.empresa.path;

const MAX_ACCOUNTS_ALLOWED = 5;

interface FormValues {
  accounts: BankAccountFormItem[];
}

/**
 * La unicidad de la CLABE necesita ver todas las cuentas; formik entrega los `values` como contexto
 * de yup, así que se consultan por ahí para evitar circularidad de tipos.
 */
const validationSchema = yup.object().shape({
  accounts: yup.array().of(
    yup.object().shape({
      clabe: yup
        .string()
        .min(CLABE_LENGTH, `Formato incorrecto, debe tener ${CLABE_LENGTH} dígitos`)
        .required("Campo requerido.")
        .test("unique", "La cuenta bancaria ya se encuentra registrada.", function (value) {
          const values = this.options.context as FormValues | undefined;
          const repetidas = values?.accounts.filter((item) => item.clabe === value).length ?? 0;
          return repetidas <= 1;
        }),
      bank_id: yup.string().required("Clabe bancaria incorrecta, validar nuevamente."),
      statements: yup.array().of(
        yup.object().shape({
          bank_statement_document_id: yup.string().required("Campo requerido."),
          document_id: yup.string().required("Campo requerido."),
          document_order: yup.number().required("Campo requerido."),
          document_value: yup.object(),
        }),
      ),
    }),
  ),
});

export const ExpInformacionBancaria = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);

  const [isLoading, setIsLoading] = useState(true);
  const [openSet, setOpenSet] = useState<Set<number>>(new Set([0]));
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingBankName, setLoadingBankName] = useState(false);
  const [loadingBankStatement, setLoadingBankStatement] = useState(false);
  const [accountIndexToDelete, setAccountIndexToDelete] = useState<number | undefined>(undefined);
  const [deleteError, setDeleteError] = useState("");
  const [numberStatements, setNumberStatements] = useState(DEFAULT_NUMBER_BANK_STATEMENTS);

  const [triggerGetBanks] = useLazyGetBanksByCodeQuery();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();
  const [getBankAccounts] = useLazyGetBankAccountsQuery();
  const [deleteBankAccount] = useDeleteBankAccountMutation();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPutBankAccount] = usePutBankAccountMutation();
  const [triggerPostBankAccount] = usePostBankAccountsMutation();
  const [triggerPatchDocument] = usePatchDocumentMutation();
  const [putApplicationCompleted] = usePutApplicationCompletedMutation();
  const [triggerPostBankAccountStatement] = usePostBankAccountStatementMutation();
  const [triggerDeleteBankAccountStatement] = useDeleteBankAccountStatementMutation();

  const volverAlExpediente = () => navigate(PATH_EXPEDIENTE);

  const formik = useFormik<FormValues>({
    initialValues: {
      accounts: [createEmptyBankAccount({ number_statements: numberStatements })],
    },
    validationSchema,
    onSubmit: async (_values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        await putApplicationCompleted({
          id_sol: applicationData.id,
          body: { bank_information_status: "COMPLETO" },
        }).unwrap();

        volverAlExpediente();
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "ExpInformacionBancaria-submit" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const { setFieldValue, setFieldTouched, setFieldError, validateForm } = formik;

  const getBankNameByCode = useCallback(
    async (bank_code: string) => {
      try {
        const bank_info = await triggerGetBanks(bank_code).unwrap();
        return bank_info ? { id: bank_info.id, name: bank_info.name } : null;
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "ExpInformacionBancaria-getBankNameByCode" } });
        return null;
      }
    },
    [triggerGetBanks],
  );

  /**
   * La CLABE es el disparador de toda la cuenta: al llegar a 18 dígitos se resuelve el banco por su
   * prefijo y la cuenta se guarda —`POST` la primera vez, `PUT` si ya tenía id—. Hasta que exista
   * ese id no se pueden subir estados de cuenta, porque cuelgan de ella.
   */
  const handleChangeCLABE = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    try {
      const { name, value } = event.target;
      const account = formik.values.accounts[index];
      const only_numbers = value.replace(/[^0-9]/g, "");
      setFieldValue(name, only_numbers);

      if (only_numbers.length !== CLABE_LENGTH) return;

      setLoadingBankName(true);
      const bank_info = await getBankNameByCode(only_numbers.substring(0, 3));

      if (bank_info) {
        const payload: PayloadBankAccount = {
          loan_application_id: applicationData.id,
          client_details_id: personalData.id,
          person_id: null,
          account: 0,
          account_holder: "",
          clabe: only_numbers,
          bank_id: bank_info.id,
          documents: [],
        };

        if (account.id) {
          await triggerPutBankAccount({
            solicitudId: applicationData.id,
            bankAccountId: account.id,
            body: payload,
          }).unwrap();
        } else {
          const response = await triggerPostBankAccount({ solicitudId: applicationData.id, body: payload }).unwrap();
          setFieldValue(`accounts[${index}].id`, response.id);
        }

        setFieldValue(`accounts[${index}].bank_id`, bank_info.id);
        setFieldValue(`accounts[${index}].bank_name`, bank_info.name || "");
      } else {
        setFieldValue(`accounts[${index}].bank_id`, "");
        setFieldValue(`accounts[${index}].bank_name`, "");
      }

      // El esquema no revalida solo tras varios `setFieldValue` seguidos.
      setTimeout(() => {
        validateForm();
        setFieldTouched(`accounts[${index}].bank_id`, true);
        setFieldTouched(`accounts[${index}].bank_name`, true);
      }, 500);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionBancaria-handleChangeCLABE" } });
    } finally {
      setLoadingBankName(false);
    }
  };

  const handleOnAddAccountBank = () => {
    if (formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED) return;
    const newIndex = formik.values.accounts.length;
    setFieldValue("accounts", [
      ...formik.values.accounts,
      createEmptyBankAccount({ number_statements: numberStatements }),
    ]);
    setOpenSet((prev) => new Set([...prev, newIndex]));
  };

  const handleOnCloseModalDelete = () => {
    setAccountIndexToDelete(undefined);
    setDeleteError("");
  };

  /** Da de baja los estados de cuenta y luego la cuenta; una cuenta con documentos validados no se toca. */
  const handleConfirmDelete = async () => {
    if (accountIndexToDelete === undefined) return;

    try {
      setIsDeleting(true);

      const copy_accounts: BankAccountFormItem[] = [...formik.values.accounts];
      const account_to_delete = copy_accounts[accountIndexToDelete];

      if (!account_to_delete || !account_to_delete.id) {
        handleOnCloseModalDelete();
        return;
      }

      const documents_validated = account_to_delete.statements.filter((item) => item?.document_value?.validated);

      if (documents_validated.length > 0) {
        setDeleteError("No se puede eliminar la cuenta: ya tiene estados de cuenta validados por operaciones.");
        return;
      }

      for (const bank_statement of account_to_delete.statements) {
        if (bank_statement?.document_id) {
          await triggerPatchDocument({
            documentId: bank_statement.document_id as UUID,
            body: { status: false },
          }).unwrap();
        }
      }

      await deleteBankAccount({ solicitudId: applicationData.id, accountId: account_to_delete.id }).unwrap();

      setFieldValue(
        "accounts",
        copy_accounts.filter((_, index) => index !== accountIndexToDelete),
      );
      handleOnCloseModalDelete();
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionBancaria-handleConfirmDelete" } });
      setDeleteError("Ha ocurrido un error al eliminar la cuenta bancaria.");
    } finally {
      setIsDeleting(false);
    }
  };

  /** Un estado de cuenta se liga a su cuenta al subirlo y se desliga al quitarlo. */
  const handleUploadBankStatement = async (
    newUpload: DocumentResponse | undefined,
    accountIndex: number,
    statementIndex: number,
  ) => {
    try {
      setLoadingBankStatement(true);

      const copy_accounts: BankAccountFormItem[] = [...formik.values.accounts];
      const account = copy_accounts[accountIndex];
      const statement = account.statements[statementIndex];

      if (!account.id) return;

      if (newUpload) {
        const response = await triggerPostBankAccountStatement({
          solicitudId: applicationData.id,
          accountId: account.id,
          body: {
            loan_application_bank_account_id: account.id,
            document_id: newUpload.id,
            document_order: statementIndex + 1,
          },
        }).unwrap();

        copy_accounts[accountIndex].statements[statementIndex] = {
          bank_statement_document_id: response.id,
          document_id: newUpload.id,
          document_order: statementIndex + 1,
          document_value: newUpload,
        };
      } else if (statement.bank_statement_document_id) {
        await triggerDeleteBankAccountStatement({
          solicitudId: applicationData.id,
          accountId: account.id,
          documentId: statement.bank_statement_document_id,
        }).unwrap();

        copy_accounts[accountIndex].statements[statementIndex] = {
          bank_statement_document_id: undefined,
          document_id: undefined,
          document_order: statementIndex + 1,
          document_value: undefined,
        };
      }

      setFieldValue("accounts", copy_accounts);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionBancaria-handleUploadBankStatement" } });
    } finally {
      setLoadingBankStatement(false);
    }
  };

  /**
   * Carga inicial: cuentas de la empresa y sus documentos. Por cada cuenta se resuelve el banco a
   * partir del prefijo de la CLABE, porque el backend solo guarda el `bank_id`.
   */
  const getInitialData = useCallback(async () => {
    try {
      setIsLoading(true);

      if (
        applicationData.bank_information_status === "COMPLETO" ||
        applicationData.bank_information_status === "VALIDADO"
      ) {
        volverAlExpediente();
        return;
      }

      const [tax_systems, list_bank_accounts, documents] = await Promise.all([
        getTaxSystems(applicationData.type).unwrap(),
        getBankAccounts(applicationData.id).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          clientDetailsId: personalData.id,
          status: true,
        }).unwrap(),
      ]);

      const { total_items } = getBankInformationConfig(tax_systems?.config_extra?.bank_information_config?.company);
      setNumberStatements(total_items);

      const { company_accounts } = identifyBankAccounts({ bank_accounts: list_bank_accounts, company: personalData });

      const accounts: BankAccountFormItem[] = [];
      const invalidIndexes: number[] = [];

      for (const account of company_accounts) {
        const clabe = (account.clabe || "").toString();
        const esInvalida = clabe.substring(0, 3) === INVALID_BANK_ACCOUNT_CODE;
        const bank_info = esInvalida ? null : await getBankNameByCode(clabe.substring(0, 3));

        const nueva: BankAccountFormItem = {
          id: account.id,
          clabe: esInvalida ? "" : clabe,
          bank_id: bank_info?.id || "",
          bank_name: bank_info?.name || "",
          statements: Array(total_items).fill(undefined),
        };

        for (let index = 0; index < total_items; index++) {
          const account_document = account.documents[index];
          if (account_document) {
            nueva.statements[index] = {
              bank_statement_document_id: account_document.bank_statement_document_id,
              document_id: account_document.document_id,
              document_order: account_document.document_order || 0,
              document_value: documents.find((item) => item.id === account_document.document_id),
            };
          }
        }

        if (esInvalida) invalidIndexes.push(accounts.length);
        accounts.push(nueva);
      }

      if (!accounts.length) {
        setFieldValue("accounts", [createEmptyBankAccount({ number_statements: total_items })]);
        setOpenSet(new Set([0]));
        return;
      }

      setFieldValue("accounts", accounts);
      setOpenSet(new Set(accounts.map((_, i) => i)));

      // Después de escribir el arreglo: si se marcara antes, `setFieldValue("accounts", …)` lo pisaría.
      invalidIndexes.forEach((index) => {
        setFieldTouched(`accounts[${index}].clabe`, true);
        setFieldError(`accounts[${index}].clabe`, "Agrega una CLABE bancaria válida.");
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpInformacionBancaria-initial_fetch" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applicationData.id,
    applicationData.type,
    personalData,
    getTaxSystems,
    getBankAccounts,
    getDocumentsByAppId,
    getBankNameByCode,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    dispatch,
  ]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const bloqueado = isLoading || isDeleting || formik.isSubmitting;

  return (
    <Box>
      <PageHeader
        eyebrow="Expediente · Empresa · Información bancaria"
        title="Información bancaria"
        subtitle="Registra la cuenta donde concentras la mayor facturación de tu negocio: ahí depositaremos tu crédito."
      />

      {isLoading ? (
        <Skeleton variant="rectangular" sx={{ height: ALTO_SKELETON_EXPEDIENTE, borderRadius: 2 }} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {formik.values.accounts.map((account, index) => (
              <AccordionCard
                key={account.id || index}
                index={index + 1}
                title={
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <span>Cuenta {index + 1}</span>
                    {index > 0 && (
                      <Button
                        tone="ghost"
                        startIcon={<DeleteOutlineOutlined sx={{ fontSize: 14 }} />}
                        disabled={bloqueado || !account.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAccountIndexToDelete(index);
                        }}
                        data-testid={`expediente-bancaria-cuenta-${index}-eliminar-button`}
                      >
                        Eliminar cuenta
                      </Button>
                    )}
                  </Box>
                }
                open={openSet.has(index)}
                onToggle={() =>
                  setOpenSet((prev) => {
                    const next = new Set(prev);
                    next.has(index) ? next.delete(index) : next.add(index);
                    return next;
                  })
                }
              >
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <FieldWrapper
                    label="Cuenta CLABE"
                    error={
                      getFormikArrayError(formik, "accounts", index, "clabe") ||
                      getFormikArrayError(formik, "accounts", index, "bank_id")
                    }
                  >
                    <TextField
                      size="lendia_input"
                      name={`accounts[${index}].clabe`}
                      fullWidth
                      autoComplete="off"
                      placeholder="18 dígitos"
                      value={account.clabe}
                      onBlur={formik.handleBlur}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeCLABE(event, index)}
                      disabled={bloqueado || loadingBankName}
                      inputProps={{
                        maxLength: CLABE_LENGTH,
                        "data-testid": `expediente-bancaria-clabe-${index}-input`,
                      }}
                    />
                  </FieldWrapper>

                  <FieldWrapper label="Banco" hint="Se identifica a partir de la CLABE.">
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      name={`accounts[${index}].bank_name`}
                      fullWidth
                      value={account.bank_name}
                      disabled
                      inputProps={{ "data-testid": `expediente-bancaria-banco-${index}-input` }}
                    />
                  </FieldWrapper>
                </Box>

                <Typography variant="body2" sx={{ color: "text.disabled", lineHeight: 1.55, mt: "16px", mb: "12px" }}>
                  Carga los estados de cuenta de los últimos {numberStatements} meses. Puedes declarar varias cuentas
                  hasta cubrir la capacidad de pago del crédito solicitado.
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {account.statements.map((statement, statementIndex) => (
                    <UploadCard
                      key={`estado_${index}_${statementIndex}`}
                      title={`Estado de cuenta ${statementIndex + 1}`}
                      required
                      fileCode={DOCUMENTS_CATALOG.estado_cuenta_bancario.code}
                      clientDetailsId={personalData.id as UUID}
                      document={statement?.document_value}
                      onDocumentChange={(newUpload) => handleUploadBankStatement(newUpload, index, statementIndex)}
                      disabled={bloqueado || loadingBankStatement || !account.id}
                    />
                  ))}
                </Box>
              </AccordionCard>
            ))}

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                tone="ghost"
                startIcon={<Add sx={{ fontSize: 13 }} />}
                disabled={
                  bloqueado ||
                  !formik.values.accounts[formik.values.accounts.length - 1]?.id ||
                  formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED
                }
                onClick={handleOnAddAccountBank}
                data-testid="expediente-bancaria-agregar-cuenta-button"
              >
                Agregar cuenta bancaria
              </Button>
            </Box>
          </Box>

          <Callout tone="neutral">
            Si la cuenta refleja menos del 50% de tus ingresos, agrega otra cuenta bancaria hasta llegar a ese
            porcentaje.
          </Callout>

          <StepActions
            onBack={volverAlExpediente}
            onNext={formik.submitForm}
            backLabel="Regresar al Expediente"
            nextLabel="Continuar"
            nextDisabled={bloqueado || !formik.isValid}
            backTestId="expediente-bancaria-back-button"
            nextTestId="expediente-bancaria-continue-button"
          />
        </Box>
      )}

      <Dialog
        open={accountIndexToDelete !== undefined}
        onClose={handleOnCloseModalDelete}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "primary.dark", textAlign: "center" }}>
            ¿Estás seguro de eliminar la cuenta bancaria?
          </Typography>
          <Typography variant="body2" sx={{ color: "text.disabled", textAlign: "center", mt: "8px" }}>
            Se darán de baja también los estados de cuenta que hayas subido en ella.
          </Typography>

          {deleteError && (
            <Box sx={{ mt: "16px" }}>
              <Callout tone="warn">{deleteError}</Callout>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1.5, mt: "24px" }}>
            <Button
              tone="ghost"
              fullWidth
              disabled={isDeleting}
              onClick={handleOnCloseModalDelete}
              data-testid="expediente-bancaria-eliminar-cancel-button"
            >
              Regresar
            </Button>
            <Button
              fullWidth
              disabled={isDeleting}
              onClick={handleConfirmDelete}
              data-testid="expediente-bancaria-eliminar-confirm-button"
            >
              Eliminar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
